import { useEffect, useRef, useState, useCallback } from "react";
import { Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [videoError, setVideoError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle video load error silently
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handleError = () => setVideoError(true);
    video.addEventListener('error', handleError);
    
    // Also check source element errors
    const source = video.querySelector('source');
    if (source) {
      source.addEventListener('error', handleError);
    }
    
    return () => {
      video.removeEventListener('error', handleError);
      if (source) {
        source.removeEventListener('error', handleError);
      }
    };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    setMousePos({ x: mouseX, y: mouseY });
  }, []);


  return (
    <section ref={containerRef} onMouseMove={handleMouseMove} className={`relative min-h-[75vh] flex items-center justify-center overflow-hidden pt-20 pb-2 transition-opacity duration-700 ${isLoaded ? "opacity-100" : "opacity-0"}`}>
      {/* Ocean Video Background with fallback */}
      <div className="absolute inset-0">
        {!videoError ? (
          <video 
            ref={videoRef}
            autoPlay 
            muted 
            loop 
            playsInline 
            preload="auto" 
            className="w-full h-full object-cover"
            onError={() => setVideoError(true)}
          >
            <source 
              src="https://videos.pexels.com/video-files/1918465/1918465-uhd_2560_1440_24fps.mp4" 
              type="video/mp4" 
            />
          </video>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 via-background to-accent/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/40 to-background/90" />
        <div className="absolute inset-0 bg-primary/5" />
      </div>


      {/* Animated Bubbles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-primary/20 backdrop-blur-sm"
            style={{
              width: `${Math.random() * 20 + 5}px`,
              height: `${Math.random() * 20 + 5}px`,
              left: `${Math.random() * 100}%`,
              bottom: `-20px`,
              animation: `bubble ${Math.random() * 5 + 5}s ease-in-out infinite ${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Floating wave lines overlay */}
      <svg className="absolute inset-0 w-full h-full opacity-20 pointer-events-none">
        <path d="M-100,400 Q200,300 500,400 T1100,400 T1700,400" fill="none" stroke="hsl(var(--primary))" strokeWidth="2" style={{ animation: "wave 8s ease-in-out infinite" }} />
        <path d="M-100,500 Q200,600 500,500 T1100,500 T1700,500" fill="none" stroke="hsl(var(--accent))" strokeWidth="2" style={{ animation: "wave 10s ease-in-out infinite reverse" }} />
        <path d="M-100,600 Q200,500 500,600 T1100,600 T1700,600" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" style={{ animation: "wave 12s ease-in-out infinite 1s" }} />
      </svg>

      {/* Cursor glow effect */}
      <div
        className="absolute w-64 h-64 rounded-full bg-primary/10 blur-3xl pointer-events-none transition-all duration-100"
        style={{
          left: mousePos.x - 128,
          top: mousePos.y - 128,
          opacity: mousePos.x > 0 ? 0.5 : 0
        }}
      />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* Main Headline - H1 for SEO - Staggered Animation */}
        <h1
          className={`text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight text-right md:text-center ${
            isLoaded ? "animate-cinematic-fade-up" : "opacity-0"
          }`}
          style={{ animationDelay: "0ms" }}
        >
          Premium Web Design for{" "}
          <span className="text-gradient-static">Hamptons Businesses & Local Brands</span>
        </h1>

        {/* Subheadline - SEO optimized - Staggered Animation */}
        <p
          className={`text-sm sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-6 ${
            isLoaded ? "animate-cinematic-fade-up" : "opacity-0"
          }`}
          style={{ animationDelay: "150ms", animationFillMode: "forwards" }}
        >
          I build high-performance, <span className="text-foreground font-semibold">SEO-optimized websites</span> for Hamptons real estate agents, contractors, and small businesses. 
          Get a <span className="text-foreground font-semibold">custom, mobile-friendly build</span> with fast turnaround—weeks, not months.
        </p>

        {/* CTA Buttons - All in one row, compact like mobile */}
        <div
          className={`flex flex-row gap-3 justify-center items-center flex-wrap ${
            isLoaded ? "animate-cinematic-fade-up" : "opacity-0"
          }`}
          style={{ animationDelay: "300ms", animationFillMode: "forwards" }}
        >
          <a
            href="mailto:aj@waveandwirewebdesigns.com?subject=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
            className="group relative px-4 py-2.5 bg-primary text-primary-foreground overflow-hidden transition-all duration-200 ease-out hover:shadow-[0_10px_40px_-10px_hsl(var(--primary)/0.5)] hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
          >
            <Mail size={14} className="pointer-events-none" />
            <span className="relative z-10 font-bold text-xs tracking-wider uppercase whitespace-nowrap pointer-events-none">
              Email Me
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-shimmer pointer-events-none" />
          </a>
          <a
            href="sms:+16317070672?body=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website."
            className="group relative px-4 py-2.5 bg-accent text-accent-foreground overflow-hidden transition-all duration-200 ease-out hover:shadow-[0_10px_40px_-10px_hsl(var(--accent)/0.5)] hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
          >
            <Phone size={14} className="pointer-events-none" />
            <span className="relative z-10 font-bold text-xs tracking-wider uppercase whitespace-nowrap pointer-events-none">
              Text Me
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-accent via-primary to-accent bg-[length:200%_100%] opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-shimmer pointer-events-none" />
          </a>
          <Link
            to="/portfolio"
            className="group px-4 py-2.5 border border-foreground/20 font-bold text-xs tracking-wider uppercase transition-all duration-200 ease-out hover:border-primary hover:text-primary hover:scale-105 hover:shadow-lg active:scale-95 flex items-center justify-center whitespace-nowrap"
          >
            View Work
          </Link>
        </div>

        {/* Contact Info Strip - Staggered Animation */}
        <div
          className={`flex flex-wrap justify-center gap-6 sm:gap-8 mt-8 sm:mt-10 ${
            isLoaded ? "animate-cinematic-fade-up" : "opacity-0"
          }`}
          style={{ animationDelay: "400ms", animationFillMode: "forwards" }}
        >
          <a
            href="mailto:aj@waveandwirewebdesigns.com?subject=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
            className="flex items-center gap-2 text-base sm:text-lg font-medium text-foreground hover:text-primary transition-all duration-200 hover:scale-105 animate-float"
            style={{ animationDuration: '6s' }}
          >
            <Mail size={20} className="animate-pulse" />
            <span>aj@waveandwirewebdesigns.com</span>
          </a>
          <a
            href="tel:+16317070672"
            className="flex items-center gap-2 text-base sm:text-lg font-medium text-foreground hover:text-primary transition-all duration-200 hover:scale-105 animate-float"
            style={{ animationDuration: '7s', animationDelay: '1s' }}
          >
            <Phone size={20} className="animate-pulse" style={{ animationDelay: '0.5s' }} />
            <span>(631) 707-0672</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hero;
